/**
 * Native keyboard wiring — the @capacitor/keyboard integration for the
 * Capacitor shell (founder-approved decision A + B-31c, 2026-07-21).
 *
 * TWO native-only responsibilities, both fail-silent and no-op off-native:
 *
 *   1. hideKeyboardAccessoryBar() — hide iOS's form-accessory bar (the
 *      up/down/Done strip WKWebView attaches above the keyboard). Decision A.
 *
 *   2. initNativeKeyboardLayout() — B-31c. Drive the LIVE keyboard height into
 *      a CSS custom property (`--tayro-kb-h`) and toggle `html.chat-kb-open`
 *      from the plugin's authoritative `keyboardWillShow` / `keyboardWillHide`
 *      events, so the chat layout can size the app-frame to end exactly at the
 *      top of the keyboard.
 *
 * WHY (2) exists — the regression it repairs: installing @capacitor/keyboard
 * DISABLES WKWebView's native auto-pan (the mechanism that used to lift the
 * focused composer above the keyboard) EVEN under capacitor.config's
 * resize:'none'. resize:'none' still holds — the web view does NOT shrink and
 * `visualViewport` does NOT report the inset on native — but with the pan gone
 * the composer was left stranded behind the keyboard (B-31b relied on that
 * pan). The plugin gives us the ONE thing the pan hack never could: the exact
 * keyboard height, up-front, via `keyboardWillShow`. We turn that into a
 * deterministic CSS layout. See chat.module.css (native kb-open rule) and
 * lib/chatKeyboard.ts (the web pan path, still authoritative on Mobile Safari
 * where no plugin exists).
 *
 * §0.7 rake — NEVER await a bare Capacitor plugin proxy. The `@capacitor/
 * keyboard` `Keyboard` export is a registerPlugin proxy: it treats EVERY
 * property access (including `.then`) as a native method, so returning it from
 * an async function makes `await` call the nonexistent native `then()` and
 * hang the caller forever. We keep the proxy out of promise-resolution's hands
 * by returning it wrapped in a `{ kb }` object — the exact pattern proven in
 * lib/run/liveActivity.ts (`{ la }`). Note `kb.addListener(...)` returns a REAL
 * promise (of a `PluginListenerHandle`) — awaiting THAT is safe; it is only the
 * bare `Keyboard` proxy that must never meet `await`.
 *
 * Fail-silent everywhere: web, older shells that predate the plugin, and the
 * non-iPhone case (`setAccessoryBarVisible` is documented iPhone-only) all
 * resolve to a quiet no-op. Nothing here may throw into app boot.
 */

import { isNativeApp } from '../platform';
import { setChatKeyboardOpen } from '../chatKeyboard';

/**
 * CSS custom property carrying the LIVE native keyboard height (px) on
 * document.documentElement while the keyboard is open; removed (→ its `0px`
 * fallback) otherwise. chat.module.css consumes it in the native kb-open rule.
 */
export const KB_HEIGHT_VAR = '--tayro-kb-h';

/** Keyboard-geometry payload from the plugin's will-show event. */
type KeyboardInfo = { keyboardHeight: number };

/** What the plugin's `addListener` resolves to — the removal handle. */
type PluginListenerHandle = { remove: () => Promise<void> };

/**
 * The plugin surface we model — deliberately only the members we call:
 * `setAccessoryBarVisible` (accessory-bar hide, decision A) plus the two
 * keyboard-lifecycle listeners that drive the deterministic-height layout.
 */
type NativeKeyboardPlugin = {
  setAccessoryBarVisible(options: { isVisible: boolean }): Promise<void>;
  addListener(
    eventName: 'keyboardWillShow',
    listenerFunc: (info: KeyboardInfo) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'keyboardWillHide',
    listenerFunc: () => void,
  ): Promise<PluginListenerHandle>;
};

// Test seam: when set (including to `null`), `plugin()` returns this instead of
// consulting `isNativeApp()` / importing the package. `undefined` = not
// injected (production path). Mirrors the __reset*ForTest hooks used elsewhere.
let injectedForTest: { kb: NativeKeyboardPlugin } | null | undefined;

/** TEST ONLY — inject a fake `{ kb }` (or `null` for the no-plugin case). */
export function __setNativeKeyboardPluginForTest(
  value: { kb: NativeKeyboardPlugin } | null | undefined,
): void {
  injectedForTest = value;
}

/**
 * Resolve the wrapped plugin, or `null` on web / import failure. The wrapper
 * object ({ kb }) is what keeps the raw proxy out of `await`'s reach.
 */
async function plugin(): Promise<{ kb: NativeKeyboardPlugin } | null> {
  if (injectedForTest !== undefined) return injectedForTest;
  if (!isNativeApp()) return null;
  try {
    const mod = (await import('@capacitor/keyboard')) as unknown as {
      Keyboard?: NativeKeyboardPlugin;
    };
    if (!mod.Keyboard) {
      console.warn('[NativeKeyboard] module loaded but Keyboard export missing');
      return null;
    }
    return { kb: mod.Keyboard };
  } catch (err) {
    console.warn(
      '[NativeKeyboard] plugin import failed:',
      err instanceof Error ? err.message : String(err),
    );
    return null;
  }
}

/**
 * Hide the iOS form-accessory bar. No-throw, no-op on web / older shells /
 * non-iPhone. Fire-and-forget at native init.
 */
export async function hideKeyboardAccessoryBar(): Promise<void> {
  const p = await plugin();
  if (!p) return;
  try {
    await p.kb.setAccessoryBarVisible({ isVisible: false });
  } catch (err) {
    console.warn(
      '[NativeKeyboard] setAccessoryBarVisible failed:',
      err instanceof Error ? err.message : String(err),
    );
  }
}

/** Set / clear the live keyboard-height var on <html>. Guarded for non-DOM. */
function setKbHeightVar(px: number | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (px === null) {
    root.style.removeProperty(KB_HEIGHT_VAR);
    return;
  }
  // Clamp to a sane non-negative pixel value; a bogus payload must never push
  // the layout taller than the viewport.
  const safe = Number.isFinite(px) && px > 0 ? px : 0;
  root.style.setProperty(KB_HEIGHT_VAR, `${safe}px`);
}

/**
 * B-31c — wire the native keyboard's LIVE geometry into the layout.
 *
 * On native, subscribe to the plugin's `keyboardWillShow` / `keyboardWillHide`
 * (fired BEFORE the animation, carrying `keyboardHeight`) and, from those
 * authoritative events:
 *   - show  → set `--tayro-kb-h` to the keyboard height and add
 *     `html.chat-kb-open` (chat layout sizes to the keyboard; BottomTab hides).
 *   - hide  → clear the var and remove the class.
 *
 * These events REPLACE the focus/blur + visualViewport heuristics ON NATIVE
 * (those remain the source of truth on web, where no plugin exists — see
 * lib/chatKeyboard.ts / Composer / Thread, all gated to skip native). Driving
 * the class from the real keyboard, not from focus, also means the tab bar
 * hides exactly when the keyboard is on screen (covering that band anyway) and
 * survives in-flight disable, which the web focus/blur path had to special-case.
 *
 * Returns a teardown that removes both listeners and clears the var/class.
 * Fire-and-forget at native init (main.tsx); no-op teardown off-native.
 *
 * §0.7 — `addListener` returns a real handle promise; we await it for the
 * removal handle. On a partial failure we still tear down whatever registered.
 */
export async function initNativeKeyboardLayout(): Promise<() => void> {
  const p = await plugin();
  if (!p) return () => {};

  const handles: PluginListenerHandle[] = [];
  const teardown = (): void => {
    for (const h of handles.splice(0)) void h.remove().catch(() => {});
    setKbHeightVar(null);
    setChatKeyboardOpen(false);
  };

  try {
    handles.push(
      await p.kb.addListener('keyboardWillShow', (info) => {
        setKbHeightVar(info?.keyboardHeight ?? 0);
        setChatKeyboardOpen(true);
      }),
    );
    handles.push(
      await p.kb.addListener('keyboardWillHide', () => {
        setKbHeightVar(null);
        setChatKeyboardOpen(false);
      }),
    );
  } catch (err) {
    console.warn(
      '[NativeKeyboard] addListener failed:',
      err instanceof Error ? err.message : String(err),
    );
    teardown();
    return () => {};
  }

  return teardown;
}
